import { isEmergencyInterruptionExpired } from './shared/helpers/emergencyInterruptionMappers';
import { InterruptionDto, InterruptionsAction, InterruptionsActionType, InterruptionsState } from './types';

export const initialInterruptionsState: InterruptionsState = {
  emergencyError: null,
  emergencyInterruptions: [],
  emergencyLoading: false,
  error: null,
  listInterruptions: [],
  loading: false,
};

const mergeEmergencyInterruptions = (currentItems: InterruptionDto[], fetchedItems: InterruptionDto[]) => {
  const activeFetchedItems = fetchedItems.filter(item => !isEmergencyInterruptionExpired(item));
  const activeCurrentItems = currentItems.filter(item => !isEmergencyInterruptionExpired(item));
  const fetchedIds = new Set(activeFetchedItems.map(item => item.id));

  return [...activeCurrentItems.filter(item => !fetchedIds.has(item.id)), ...activeFetchedItems];
};

export const interruptionsReducer = (state: InterruptionsState, action: InterruptionsAction): InterruptionsState => {
  switch (action.type) {
    case InterruptionsActionType.Reset:
      return initialInterruptionsState;
    case InterruptionsActionType.SetInterruptionsRequest:
      return {
        ...state,
        error: null,
        loading: true,
      };
    case InterruptionsActionType.SetInterruptionsSuccess:
      return {
        ...state,
        error: null,
        listInterruptions: action.payload || [],
        loading: false,
      };
    case InterruptionsActionType.SetInterruptionsFailure:
      return {
        ...state,
        error: action.payload || 'Failed to fetch interruptions',
        loading: false,
      };
    case InterruptionsActionType.AddInterruption: {
      return {
        ...state,
        listInterruptions: [action.payload, ...state.listInterruptions.filter(item => item.id !== action.payload.id)],
      };
    }
    case InterruptionsActionType.RemoveInterruption:
      return {
        ...state,
        listInterruptions: state.listInterruptions.filter(item => item.id !== action.payload),
      };
    case InterruptionsActionType.SetInterruptionResponseStatus:
      return {
        ...state,
        listInterruptions: state.listInterruptions.map(item =>
          item.id === action.payload.id
            ? { ...item, employeeResponseStatus: action.payload.employeeResponseStatus }
            : item,
        ),
        emergencyInterruptions: state.emergencyInterruptions.map(item =>
          item.id === action.payload.id
            ? { ...item, employeeResponseStatus: action.payload.employeeResponseStatus }
            : item,
        ),
      };
    case InterruptionsActionType.SetInterruptionsViewStatus: {
      const idsSet = new Set(action.payload.ids);

      return {
        ...state,
        listInterruptions: state.listInterruptions.map(item =>
          idsSet.has(item.id) ? { ...item, viewStatus: action.payload.viewStatus } : item,
        ),
      };
    }
    case InterruptionsActionType.SetEmergencyInterruptionsRequest:
      return {
        ...state,
        emergencyError: null,
        emergencyLoading: true,
      };
    case InterruptionsActionType.SetEmergencyInterruptionsSuccess:
      return {
        ...state,
        emergencyError: null,
        emergencyInterruptions: action.payload,
        emergencyLoading: false,
      };
    case InterruptionsActionType.MergeEmergencyInterruptionsSuccess:
      return {
        ...state,
        emergencyError: null,
        emergencyInterruptions: mergeEmergencyInterruptions(state.emergencyInterruptions, action.payload),
        emergencyLoading: false,
      };
    case InterruptionsActionType.SetEmergencyInterruptionsFailure:
      return {
        ...state,
        emergencyError: action.payload || 'Failed to fetch emergency interruptions',
        emergencyLoading: false,
      };
    case InterruptionsActionType.RemoveEmergencyInterruption:
      return {
        ...state,
        emergencyInterruptions: state.emergencyInterruptions.filter(item => item.id !== action.payload),
      };
    case InterruptionsActionType.AddEmergencyInterruption:
      return {
        ...state,
        emergencyInterruptions: [
          action.payload,
          ...state.emergencyInterruptions.filter(item => item.id !== action.payload.id),
        ],
      };
    default:
      return state;
  }
};
